/**
 * Tiny three-dot menu for table rows. Self-contained — opens/closes via
 * click, dismisses on outside click or Escape. Items can be flagged
 * `tone: 'danger'` for destructive actions.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';

export interface RowMenuItem {
  label: string;
  icon?: 'check' | 'x' | 'download' | 'mail' | 'arrow-right' | 'sparkle';
  tone?: 'default' | 'danger';
  disabled?: boolean;
  onSelect: () => void;
}

interface Props {
  items: RowMenuItem[];
  label?: string;
  /** Optional custom trigger; default is a three-dot icon button. */
  trigger?: ReactNode;
}

export function RowMenu({ items, label = "Plus d'actions", trigger }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="row-menu" ref={wrapRef}>
      <button
        type="button"
        className="row-menu-trigger"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
      >
        {trigger ?? <Icon name="menu-dots" size={16} />}
      </button>
      {open && (
        <div className="row-menu-pop" role="menu">
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`row-menu-item${item.tone === 'danger' ? ' danger' : ''}`}
              disabled={item.disabled}
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
                if (!item.disabled) item.onSelect();
              }}
            >
              {item.icon && <Icon name={item.icon} size={14} />}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
